import React, { useState } from 'react';
import { message, Form, Input, Button, Space, Select, Modal } from 'antd';
import { apiClient, apiErrorMessage } from '../../../utils';
import { CheckOutlined, ExclamationCircleOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useForm } from 'antd/lib/form/Form';
import { Grid } from '../../../models';

export function NewIntegrationPage(props: { onSaved: () => void }) {
  const { Option } = Select;

  const [form] = useForm();
  const [integrationType, setIntegrationType] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columns, setColumns] = useState<string[]>();
  const [grids, setGrids] = useState<Grid[]>();
  const [gridsLoading, setGridsLoading] = useState(false);

  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };

  const formItemLayoutWithOutLabel = {
    wrapperCol: { span: 18, offset: 6 },
  };

  const filterNestedColumns = (columns: string[]): string[] => {
    return columns.filter(col => {
      // Check if this column is part of a nested structure
      const isNested = col.includes('.');
      if (isNested) {
        return false;
      }
      // Check if this column has any subcolumns
      const hasSubcolumns = columns.some(c => c !== col && c.startsWith(col + '.'));
      return !hasSubcolumns;
    });
  };

  const onIntegrationTypeChange = (value: string) => {
    setIntegrationType(value);
    if (value === 'EXPORT') {
      setGridsLoading(true);
      apiClient
        .get('grids')
        .then(response => {
          setGrids(response.data as Grid[]);
          setGridsLoading(false);
        })
        .catch((reason: any) => {
          setGridsLoading(false);
          apiErrorMessage(reason);
        });
    }
  };

  const onValuesChange = (changedValues: any) => {
    if (changedValues.url) {
      setColumns(undefined);
      form.resetFields(['identifierColumns']);
    }
  };

  const loadColumns = () => {
    if (columnsLoading) {
      return;
    }
    form
      .validateFields(['url'])
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        if (form.getFieldError('url').length) {
          return;
        }
        setColumnsLoading(true);
        const values = form.getFieldsValue();
        const body = {
          url: values.url,
          headers: values.headers,
        };
        apiClient
          .post('integrations/columns', body)
          .then(response => {
            const filteredColumns = filterNestedColumns(response.data);
            setColumns(filteredColumns);
          })
          .catch(() => {
            setColumns(undefined);
            form.resetFields(['identifierColumns']);
            message.error('Kaynak veriye ulaşılamadı. Lütfen bağlantı bilgilerini kontrol edin.');
          })
          .finally(() => {
            setColumnsLoading(false);
          });
      });
  };

  const confirmUrlUsage = (values: any) => {
    const onOk = () => submitForm(values);
    Modal.confirm({
      title: 'URL',
      icon: <ExclamationCircleOutlined />,
      content: 'Girdiğiniz URL daha önce başka bir entegrasyonda kullanılmış, devam etmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const submitForm = (values: any) => {
    setLoading(true);
    apiClient
      .post('integrations', values)
      .then(() => {
        setLoading(false);
        props.onSaved();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const onFinish = (values: any) => {
    setLoading(true);
    apiClient
      .get('integrations/check?url=' + values.url)
      .then((response: any) => {
        setLoading(false);
        if (response.data === true) {
          confirmUrlUsage(values);
        } else {
          submitForm(values);
        }
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  return (
    <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} onFinish={onFinish} onValuesChange={onValuesChange}>
      <Form.Item label="Tür" name="integrationType" rules={[{ required: true, message: 'Zorunlu alan' }]}>
        <Select onChange={onIntegrationTypeChange}>
          <Select.Option value="IMPORT">İçe entegrasyon</Select.Option>
          <Select.Option value="EXPORT">Dışa entegrasyon</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        label="İsim"
        name="name"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          {
            pattern: /^[a-zA-Z0-9ıİğĞüÜşŞöÖçÇ][a-zA-Z0-9_\-ıİğĞüÜşŞöÖçÇ\s]*$/,
            message: 'Sadece harf, rakam, boşluk, - ve _ karakteri kullanılabilir',
          },
          { max: 100, message: 'En fazla 100 karakter' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Açıklama"
        name="description"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 400, message: 'En fazla 400 karakter' },
        ]}
      >
        <Input.TextArea />
      </Form.Item>
      <Form.Item
        label="URL"
        name="url"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          {
            pattern: /https?:\/\/((localhost:\d{4})|(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6})\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)/,
            message: 'Geçersiz URL',
          },
          { max: 500, message: 'En fazla 500 karakter' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.List name="headers">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <Form.Item
                {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                label={index === 0 ? 'Başlıklar' : ''}
                required={false}
                key={field.key}
                style={{ marginBottom: 0 }}
              >
                <Space key={field.key} align="baseline">
                  <Form.Item {...field} name={[field.name, 'name']} rules={[{ required: true, message: 'Zorunlu alan' }]}>
                    <Input placeholder="İsim" />
                  </Form.Item>
                  <Form.Item {...field} name={[field.name, 'value']} rules={[{ required: true, message: 'Zorunlu alan' }]}>
                    <Input placeholder="Değer" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(field.name)} />
                </Space>
              </Form.Item>
            ))}
            <Form.Item {...formItemLayoutWithOutLabel}>
              <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                İstek başlığı ekle
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
      {integrationType === 'IMPORT' && (
        <>
          <Form.Item label="Bağlantı testi" required={true}>
            <Button loading={columnsLoading} onClick={loadColumns} icon={columns && columns?.length > 0 ? <CheckOutlined /> : null}>
              {columns && columns.length > 0 ? 'Bağlantı başarılı' : 'Şimdi dene'}
            </Button>
          </Form.Item>
          <Form.Item label="Tanım veri elemanları" name="identifierColumns" rules={[{ required: true, message: 'Zorunlu alan' }]}>
            <Select mode="multiple" allowClear disabled={columns === undefined}>
              {columns?.map(c => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </>
      )}
      {integrationType === 'EXPORT' && (
        <>
          <Form.Item label="Ana veri seti" name="primaryGridId" rules={[{ required: true, message: 'Zorunlu alan' }]}>
            <Select loading={gridsLoading}>
              {grids?.map(g => (
                <Option key={g.id} value={g.id}>
                  {g.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </>
      )}
      <Form.Item {...formItemLayoutWithOutLabel}>
        <Button type="primary" htmlType="submit" loading={loading}>
          Ekle
        </Button>
      </Form.Item>
    </Form>
  );
}
